import React, { useRef, useState, useEffect } from 'react';
import { ListItem, Typography, IconButton, Chip, Box } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import HistoryItemMenu from './HistoryItemMenu';
import { useNavigate } from 'react-router-dom';
import { getLanguageByCode } from '../../constants';
import { useTranslation } from 'react-i18next';

const HistoryListItem = ({
  item,
  idx,
  selected,
  onSelect,
  onMoreClick,
  refFn,
  projects,
  handleAddHistoryToProject,
  handleEditHistory,
  handleDeleteClick,
  menuOpenIdx,
  setMenuOpenIdx,
  listIdx,
  setHistory,
  isEditing,
  setAnalyzedVideoId,
  setAnalyzedTitle,
  setAnalyzedUrl,
  setDrawerOpen
}) => {
  const moreBtnRef = useRef();
  const menuRef = useRef();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    const handleClick = (e) => {
      if (menuOpenIdx === listIdx && menuRef.current && !menuRef.current.contains(e.target) && !moreBtnRef.current.contains(e.target)) {
        setMenuOpenIdx(-1);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpenIdx, listIdx, setMenuOpenIdx]);

  const handleItemClick = async () => {
    try {
      // 현재 경로가 이미 해당 히스토리 페이지인지 확인
      const currentPath = window.location.pathname;
      const targetPath = `/analyze/${item.id}`;
      
      // 히스토리의 언어 정보 확인
      const historyLanguage = item.language || 'ko'; // 기본값: 한국어
      const currentLanguage = i18n.language;
      
      console.log(`🌍 [히스토리 클릭] 언어 확인: 현재=${currentLanguage}, 히스토리=${historyLanguage}`);
      
      // 언어가 다른 경우 히스토리의 언어로 변경
      if (historyLanguage !== currentLanguage) {
        console.log(`🔄 [히스토리 클릭] 언어 변경: ${currentLanguage} → ${historyLanguage}`);
        await i18n.changeLanguage(historyLanguage);
      }
      
      // onSelect가 있으면 호출 (드로어 닫기 등)
      if (onSelect) {
        onSelect();
      }
      
      if (currentPath === targetPath) {
        // 같은 페이지면 아무것도 하지 않음 (이미 올바른 상태)
        return;
      }
      
      // 상태 변경 없이 바로 페이지 이동만 수행
      // 이렇게 하면 현재 페이지에서 깜빡임 없이 바로 새 페이지로 이동
      navigate(targetPath, { replace: true });
    } catch (error) {
      console.error('Navigation error:', error);
      // 에러 발생 시 현재 페이지 새로고침
      window.location.reload();
    }
  };

  return (
    <ListItem
      key={item.id || idx}
      disablePadding
      sx={{
        display: 'flex',
        minHeight: 44,
        py: 1.2,
        px: 1.2,
        gap: 1.2,
        alignItems: 'center',
        bgcolor: selected ? 'rgba(25, 118, 210, 0.13)' : 'transparent',
        '&:hover': {
          bgcolor: selected ? 'rgba(25, 118, 210, 0.18)' : 'rgba(0, 0, 0, 0.04)'
        },
        borderRadius: 1,
        transition: 'background 0.2s',
        position: 'relative',
        overflow: 'visible',
        minWidth: 0,
        cursor: 'pointer'
      }}
      onClick={handleItemClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, pr: 4 }}>
        <Typography
          noWrap
          sx={{
            fontSize: 13,
            color: selected ? '#1976d2' : '#222',
            flex: 1,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: selected ? 600 : 400
          }}
        >
          {item.customTitle || item.title || '제목을 불러올 수 없음'}
        </Typography>
        <Box
          sx={{
            fontSize: '1.1em',
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 20,
            height: 18
          }}
        >
          {getLanguageByCode(item.language || 'ko').flag}
        </Box>
      </Box>
      <IconButton
        className="history-more"
        size="small"
        sx={{
          color: '#888',
          p: 0.5,
          ml: 0.5,
          opacity: 1,
          transition: 'opacity 0.2s',
          position: 'absolute',
          right: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.08)'
          }
        }}
        ref={el => {
          moreBtnRef.current = el;
          if (refFn) refFn(el);
        }}
        onClick={e => {
          e.stopPropagation();
          if (isEditing) return;
          setTimeout(() => {
            setMenuOpenIdx(item.id);
          }, 0);
          onMoreClick(e);
        }}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <HistoryItemMenu
        anchorEl={menuOpenIdx === item.id ? moreBtnRef.current : null}
        onClose={() => setMenuOpenIdx(null)}
        handleEditHistory={handleEditHistory}
        handleDeleteClick={(id, e) => handleDeleteClick(id, e)}
        projects={projects}
        handleAddHistoryToProject={handleAddHistoryToProject}
        historyId={item.id}
        currentTitle={item.customTitle}
      />
    </ListItem>
  );
};

export default HistoryListItem; 